import { HugeiconsIcon } from "@hugeicons/react";
import { GridIcon } from "@hugeicons-pro/core-bulk-rounded";

import { PageHeader } from "@/components/layout/page-header";
import { GlobalFilterBar } from "@/components/filters/global-filter-bar";
import { ChartCard } from "@/components/dashboard/chart-card";
import { CategoryBars } from "@/components/dashboard/charts";
import { BreakdownTable } from "@/components/dashboard/breakdown-table";
import { platformBrandColor } from "@/components/ui/platform-logo";
import { platformLabelCol, spendCol, revenueCol, roasCol, impressionsCol, clicksCol, ctrCol, exactReachCol } from "@/components/dashboard/columns";
import { useExactReach } from "@/lib/use-reach";
import { Skeleton } from "@/components/ui/skeleton";
import { useBreakdown } from "@/lib/use-metrics";
import { useReference, channelRows } from "@/lib/use-reference";
import { useMetricMoney } from "@/lib/use-money";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useRollupQuery } from "@/lib/use-metrics";
import { supabase } from "@/lib/supabase";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getBingPeriod } from '@/lib/api/bing-period';
import { matchesSegment, type CampaignSegment } from '@/lib/api/campaigns';

export function Platforms() {
  const m = useMetricMoney();
  const { platforms } = useReference();
  const byPlatform = useBreakdown("platform");
  const q = useRollupQuery();
  const [campaignType, setCampaignType] = useState("all");
  // Period reach only exists per platform account, not per campaign type.
  const reach = useExactReach({ enabled: campaignType === "all" });
  const showMicrosoft = campaignType !== 'all' && (q.channel === 'all' || platforms.find(p => p.id === q.channel)?.key === 'bing');
  const microsoft = useQuery({
    queryKey: ['bing-period',q.from,q.to], enabled: showMicrosoft,
    queryFn: () => getBingPeriod(q.from,q.to),
    refetchInterval: query => query.state.data?.status === 'pending' ? 5000 : false,
  });
  const nativeMicrosoft = (microsoft.data?.rows ?? []).filter(c =>
    (q.region === 'all' || c.region === q.region) && (q.brand === 'all' || c.brand_id === q.brand) &&
    (q.activity === 'all' || (q.activity === 'non_ecom' ? c.activity_type !== 'ecom' : c.activity_type === q.activity)) &&
    matchesSegment(c,campaignType as CampaignSegment));
  const nativeTotals = [...new Set(nativeMicrosoft.map(c => c.currency))].map(currency => {
    const selected = nativeMicrosoft.filter(c => c.currency === currency);
    return {currency,spend:selected.reduce((s,c)=>s+Number(c.spend),0),revenue:selected.reduce((s,c)=>s+Number(c.revenue),0),
      impressions:selected.reduce((s,c)=>s+Number(c.impressions),0),clicks:selected.reduce((s,c)=>s+Number(c.clicks),0)};
  });
  const comparison = useQuery({
    queryKey: ['campaign-type',q,campaignType], enabled: campaignType !== 'all',
    queryFn: async () => {
      const {data,error} = await supabase.rpc('rpc_campaign_type_totals', {
        p_from:q.from,p_to:q.to,p_region:q.region,p_brand:q.brand==='all'?null:q.brand,
        p_platform:q.channel==='all'?null:q.channel,p_activity:q.activity,p_currency:q.currency,p_segment:campaignType,
      });
      if(error) throw error;
      return data as {key:string;spend:number;revenue:number;impressions:number;clicks:number;conversions:number}[];
    },
  });
  const loading = byPlatform.isPending || (campaignType !== 'all' && comparison.isPending);

  // Only real channels here — GA4 are tools, shown under Bronnen.
  const baseRows = channelRows(byPlatform.data ?? [], platforms);
  const rows = campaignType === 'all' ? baseRows : baseRows.flatMap(row => {
    const c = comparison.data?.find(c => c.key === row.key);
    if(!c) return [];
    const spend=Number(c.spend),revenue=Number(c.revenue),clicks=Number(c.clicks),impressions=Number(c.impressions);
    return [{...row,spend,revenue,clicks,impressions,orders:Number(c.conversions),
      roas:spend>0?revenue/spend:null,ctr:impressions>0?clicks/impressions:null,reached_accounts:0}];
  });

  const columns = [
    platformLabelCol("Platform", platforms), spendCol(m.fmt), revenueCol(m.fmt), roasCol(), impressionsCol(), clicksCol(), ctrCol(),
    ...(campaignType === "all" ? [exactReachCol(reach, "platform")] : []),
  ];

  const platByLabel = (label: string) => platforms.find((p) => p.name === label);
  const spendBars = rows.filter((r) => r.spend > 0).map((r) => ({ label: r.label, spend: r.spend }));
  const barColors = spendBars.map((b) => { const p = platByLabel(b.label); return platformBrandColor(p?.key ?? b.label, p?.accent_color); });
  const logoFor = (label: string) => { const p = platByLabel(label); return p ? { platform: p.key, accent: p.accent_color } : undefined; };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Platformen"
        icon={GridIcon}
        title="Platformen"
        description="Performance per channel. Tools (GA4) staan onder Bronnen."
        tabs={<div className="flex flex-wrap gap-2"><GlobalFilterBar />
          <Select value={campaignType} onValueChange={v=>setCampaignType(v??'all')}>
            <SelectTrigger className="w-48" aria-label="Campagnetype"><SelectValue>{campaignType === 'all' ? 'Alle campagnetypes' : campaignType === 'search' ? 'Search' : 'Shopping'}</SelectValue></SelectTrigger>
            <SelectPopup><SelectItem value="all">Alle campagnetypes</SelectItem><SelectItem value="search">Search</SelectItem><SelectItem value="shopping">Shopping</SelectItem></SelectPopup>
          </Select>
        </div>}
      />

      {loading ? (
        <Skeleton className="h-64 rounded-2xl" />
      ) : (
        <ChartCard title="Uitgaven per channel" bodyClassName="h-80">
          <CategoryBars data={spendBars} dataKey="spend" kind="currency" colors={barColors} logoFor={logoFor} moneyFmt={m.fmt} />
        </ChartCard>
      )}

      {comparison.isError && campaignType !== 'all' ? <p role="alert">Campagnevergelijking kon niet worden geladen.</p> :
      <BreakdownTable title="Per channel" description={campaignType === 'all'
        ? "Alle campagnetypes samen. Kanaalattributie is geen unieke webshopomzet. Vergelijk een Search-selectie in het advertentieaccount met de Search-filter hierboven."
        : "Gemeten campagnecijfers voor het gekozen type op basis van de campagnenaam. Conversiewaarde volgt het advertentieplatform, met ECB-dagkoersen voor de weergegeven valuta."} rows={rows} columns={columns} rowKey={(r) => r.key} exportName="Prado Pulse — Per channel" />}
      {showMicrosoft && <section className="rounded-2xl border bg-card p-5 space-y-3">
        <h2 className="font-semibold">Microsoft Ads: periodeverslag</h2>
        <p className="text-sm text-muted-foreground">Rechtstreeks uit Microsoft, voor dezelfde datums en filters. Bedragen in de accountvaluta, zonder omrekening. Het dagoverzicht hierboven kan door Microsofts afronding per dag enkele centen afwijken.</p>
        {microsoft.isError ? <p role="alert">Het Microsoft-verslag kon niet worden opgehaald. De dagtotalen worden niet als exact periodeverslag gepresenteerd.</p>
          : microsoft.data?.status !== 'ready' ? <p className="text-sm">Microsoft stelt het periodeverslag samen…</p>
          : nativeTotals.length === 0 ? <p className="text-sm">Geen Microsoft-campagnes voor deze selectie.</p>
          : <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b text-left"><th className="py-2">Valuta</th><th>Uitgaven</th><th>Conversiewaarde</th><th>Impressies</th><th>Kliks</th></tr></thead><tbody>
            {nativeTotals.map(r=><tr key={r.currency} className="border-b last:border-0"><td className="py-3">{r.currency}</td><td>{r.spend.toLocaleString('nl-BE',{minimumFractionDigits:2,maximumFractionDigits:2})}</td><td>{r.revenue.toLocaleString('nl-BE',{minimumFractionDigits:2,maximumFractionDigits:2})}</td><td>{r.impressions.toLocaleString('nl-BE')}</td><td>{r.clicks.toLocaleString('nl-BE')}</td></tr>)}
          </tbody></table></div>}
      </section>}
    </div>
  );
}
